import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { store } from '@/App/store'; 
import { LOGOUT } from '@/features/Auth/authSlice';
import { showErrorToast } from '@/components/Toastr/actions';
import { push } from '@/App/router/actions';

const ENV_SSO_URI = process.env.REACT_APP_SSO_URI;
export const REACT_APP_SSO_URI = ENV_SSO_URI || 'https://sso.noviopus.com/api';

const REACT_APP_BASE_API = process.env.REACT_APP_BASE_API || '/api';
const MARKETING_URL = process.env.REACT_APP_MARKETING_APP || 'https://noviopus.com';

const instance: AxiosInstance = axios.create({
  baseURL: REACT_APP_BASE_API,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;
    const url = originalRequest.url || '';

    // 1. CRITICAL: Prevent Infinite Loops
    // Check for both relative and absolute paths
    if (url.includes('auth/refresh') || url.includes('signout')) {
        return Promise.reject(error);
    }

    // 2. Handle 402 (Payment Required / Paywall Interceptor)
    if (error.response && error.response.status === 402) {
      // Safely extract the exact error message the backend threw
      const errorData: any = error.response.data;
      const errorMsg = errorData?.error?.message || errorData?.error || errorData?.message || 'Action blocked: Free tier limit reached.';
      
      console.warn('[Request] 402 Payment Required. Redirecting to Paywall...');
      
      if (store) {
          // Fire the internal custom toastr
          store.dispatch(showErrorToast(errorMsg));
          
          // Forcefully redirect the user to the Paywall
          store.dispatch(push('/settings/billing/activate?reason=limit_reached'));
      }
      
      return Promise.reject(error);
    }

    // 3. Handle 401 (Unauthorized)
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => instance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshUrl = `${REACT_APP_SSO_URI}/users/auth/refresh`;
        await axios.post(refreshUrl, {}, { withCredentials: true });
        
        processQueue(null, 'refreshed');
        return instance(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        
        console.warn('[Request] Refresh Failed. Triggering Logout.');
        
        if (store) {
           store.dispatch({ type: LOGOUT });
        } else {
           window.location.href = MARKETING_URL;
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

function parseJSON(response: any) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.data;
}

function checkStatus(response: any) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error: any = new Error(response.statusText);
  error.response = response;
  throw error;
}

export default function request(url: string, options?: AxiosRequestConfig) {
  return instance(url, options)
    .then(checkStatus)
    .then(parseJSON);
}

export const internalApi = instance;